/**
 * Layout rendering engine.
 *
 * Traverses a LayoutDocument tree and renders each node by looking it up in
 * the component registry. Nodes are positioned absolutely on the canvas
 * (or via flex when inside a container with layout === "flex").
 *
 * This component is **data-agnostic**: it receives already-collected
 * InjectedData from the host app's DataProvider.
 */

import { Box } from "@mui/material";
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  ComponentNode,
  ContainerNode,
  InjectedData,
  LayoutListComponentProps,
  LayoutDocument,
  LayoutNode,
  SceneBehaviorCatalog,
  TransitionConfig,
} from "./layout-types";
import { resolveStyles } from "./layout-types";
import { useBehavior } from "./useBehavior.js";
import { useSceneCustomEventGraphs } from "./useSceneCustomEventGraphs.js";
import { useSceneFunctionGraphs } from "./useSceneFunctionGraphs.js";
import { useSceneVariableGraphs } from "./useSceneVariableGraphs.js";
import { useBehaviorDrainCoordinator } from "./BehaviorDrainCoordinatorContext.js";
import { BehaviorDrainCoordinatorProvider } from "./BehaviorDrainCoordinatorContext.js";
import { LayoutRenderScaleContext } from "./render-scale-context";
import { getRenderer, nodeKey } from "./registry";
import { setupRegistry } from "./setup-registry";
import type { BarLoaderTargetPatch } from "./components/barLoaderConfig.js";
import { extractSceneAnimations } from "../../../util/src/blueprint/runtime/scene-animations.js";
import { behaviorAnimationToWebAnimation } from "./useAnimation.js";
import { applySpacingDeltaToSize, hasSpacingPatch } from "./spacingSizing";
import {
  LayoutList,
  type LayoutListClipRect as ClipRect,
} from "./components/LayoutList";
import {
  resolveBarLoaderVariableFootprintWidth,
  type BarLoaderProps,
} from "./components/barLoaderConfig";
import { useLayoutFontFacesProvider } from "./DataProviderContext";
import { useLayoutFontFacesLoader } from "./useLayoutFontFacesLoader.js";
import {
  isProtectedMediaSrc,
  ProtectedMediaCacheContext,
  useProtectedMediaCache,
} from "./ProtectedMediaCacheContext";
import {
  mergeBehaviorComponent,
  mergeBehaviorLocation,
  mergeBehaviorStyles,
  mergeBehaviorTransform,
} from "./resolveBehaviorStyles.js";
import { useRuntimeLayoutFullscreenMenu } from "./useRuntimeLayoutFullscreenMenu.js";
import { hasTextGradientFill } from "../../../util/src/layout/text-gradient-fill.js";

/**
 * Build a CSS `transition` shorthand from node-level transition entries.
 */
export function buildCssTransition(
  transitions: TransitionConfig[] | undefined,
): string | undefined {
  if (!transitions?.length) return undefined;
  const enabledTransitions = transitions.filter(
    (transition) => transition?.enabled === true && transition.property,
  );
  if (enabledTransitions.length === 0) return undefined;
  return enabledTransitions
    .map((transition) => {
      const duration = `${(transition.durationMs ?? 300) / 1000}s`;
      const easing = transition.easing || "ease-out";
      const delay = transition.delayMs ? `${transition.delayMs / 1000}s` : "0s";
      return `${transition.property} ${duration} ${easing} ${delay}`;
    })
    .join(", ");
}

// Ensure built-in components are registered.
setupRegistry();

const SKEW_X_REGEX = /skewX\(\s*(-?\d+(?:\.\d+)?)deg\s*\)/i;
const SKEW_Y_REGEX = /skewY\(\s*(-?\d+(?:\.\d+)?)deg\s*\)/i;
const ROTATE_REGEX = /rotate\(\s*(-?\d+(?:\.\d+)?)deg\s*\)/gi;
function parseSkewAxis(transform: string | undefined, axis: "x" | "y"): number {
  if (!transform) return 0;
  const match = (axis === "x" ? SKEW_X_REGEX : SKEW_Y_REGEX).exec(transform);
  const value = Number(match?.[1] ?? 0);
  if (!Number.isFinite(value)) return 0;
  return Math.round(value);
}

function composeNodeTransform(
  baseTransform: string | undefined,
  rotation: number | undefined,
): string | undefined {
  const normalizedTransform =
    typeof baseTransform === "string" && baseTransform.trim().length > 0
      ? baseTransform.trim()
      : undefined;
  const normalizedRotation =
    typeof rotation === "number" && Number.isFinite(rotation) ? rotation : 0;

  if (!normalizedTransform && normalizedRotation === 0) return undefined;
  if (!normalizedTransform) return `rotate(${normalizedRotation}deg)`;
  if (normalizedRotation === 0) return normalizedTransform;

  return `${normalizedTransform} rotate(${normalizedRotation}deg)`;
}

export function parseRotationDegrees(transform: string | undefined): number {
  if (!transform) return 0;
  let match: RegExpExecArray | null = null;
  let rotation = 0;
  ROTATE_REGEX.lastIndex = 0;
  while (true) {
    match = ROTATE_REGEX.exec(transform);
    if (!match) break;
    const value = Number(match[1] ?? 0);
    if (Number.isFinite(value)) rotation += value;
  }
  return rotation;
}

function intersectClipRects(
  baseClip: ClipRect | undefined,
  nextClip: ClipRect,
): ClipRect {
  if (!baseClip) return nextClip;
  return {
    left: Math.max(baseClip.left, nextClip.left),
    top: Math.max(baseClip.top, nextClip.top),
    right: Math.min(baseClip.right, nextClip.right),
    bottom: Math.min(baseClip.bottom, nextClip.bottom),
  };
}

export function buildNodeClipPath(
  clipRect: ClipRect,
  canvasNodeLeft: number,
  canvasNodeTop: number,
  nodeWidth: number,
  nodeHeight: number,
  scale: number,
  rotationDeg: number,
): string {
  const nodeRight = canvasNodeLeft + nodeWidth;
  const nodeBottom = canvasNodeTop + nodeHeight;
  const intersectLeft = Math.max(clipRect.left, canvasNodeLeft);
  const intersectTop = Math.max(clipRect.top, canvasNodeTop);
  const intersectRight = Math.min(clipRect.right, nodeRight);
  const intersectBottom = Math.min(clipRect.bottom, nodeBottom);
  const localClip: ClipRect = {
    left: intersectLeft - canvasNodeLeft,
    top: intersectTop - canvasNodeTop,
    right: intersectRight - canvasNodeLeft,
    bottom: intersectBottom - canvasNodeTop,
  };
  return clipRectToPolygonForNode(
    localClip,
    0,
    0,
    nodeWidth,
    nodeHeight,
    scale,
    rotationDeg,
  );
}

function clipRectToPolygonForNode(
  clipRect: ClipRect,
  nodeLeft: number,
  nodeTop: number,
  nodeWidth: number,
  nodeHeight: number,
  scale: number,
  rotationDeg: number,
): string {
  if (
    nodeWidth <= 0 ||
    nodeHeight <= 0 ||
    clipRect.right <= clipRect.left ||
    clipRect.bottom <= clipRect.top
  ) {
    return "polygon(0px 0px, 0px 0px, 0px 0px, 0px 0px)";
  }

  const centerX = nodeLeft + nodeWidth / 2;
  const centerY = nodeTop + nodeHeight / 2;
  const theta = (-rotationDeg * Math.PI) / 180;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);

  const clipCorners = [
    { x: clipRect.left, y: clipRect.top },
    { x: clipRect.right, y: clipRect.top },
    { x: clipRect.right, y: clipRect.bottom },
    { x: clipRect.left, y: clipRect.bottom },
  ];

  const localPoints = clipCorners.map((point) => {
    const dx = point.x - centerX;
    const dy = point.y - centerY;
    const unrotatedX = centerX + dx * cos - dy * sin;
    const unrotatedY = centerY + dx * sin + dy * cos;
    return {
      x: (unrotatedX - nodeLeft) * scale,
      y: (unrotatedY - nodeTop) * scale,
    };
  });

  return `polygon(${localPoints
    .map((point) => `${point.x}px ${point.y}px`)
    .join(", ")})`;
}

function toCssLength(value: unknown): string | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return `${Math.max(0, value)}px`;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    return value.trim();
  }
  return undefined;
}

function toScaledCssLength(value: unknown, scale: number): string | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return `${Math.max(0, value) * scale}px`;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const trimmed = value.trim();
    if (trimmed.endsWith("px")) {
      const parsed = Number.parseFloat(trimmed.replace("px", "").trim());
      if (Number.isFinite(parsed)) {
        return `${Math.max(0, parsed) * scale}px`;
      }
    }
    return trimmed;
  }
  return undefined;
}

function getResolvedBorderSx(resolvedStyle: Record<string, unknown>) {
  return {
    border: resolvedStyle.border as string | undefined,
    borderTop: resolvedStyle.borderTop as string | undefined,
    borderRight: resolvedStyle.borderRight as string | undefined,
    borderBottom: resolvedStyle.borderBottom as string | undefined,
    borderLeft: resolvedStyle.borderLeft as string | undefined,
    borderRadius: toCssLength(resolvedStyle.borderRadius),
    borderTopLeftRadius: toCssLength(resolvedStyle.borderTopLeftRadius),
    borderTopRightRadius: toCssLength(resolvedStyle.borderTopRightRadius),
    borderBottomRightRadius: toCssLength(resolvedStyle.borderBottomRightRadius),
    borderBottomLeftRadius: toCssLength(resolvedStyle.borderBottomLeftRadius),
  };
}

function getResolvedSpacingSx(
  resolvedStyle: Record<string, unknown>,
  scale: number,
  options?: { includeMargin?: boolean },
) {
  const includeMargin = options?.includeMargin ?? true;
  return {
    margin: includeMargin
      ? toScaledCssLength(resolvedStyle.margin, scale)
      : undefined,
    marginTop: includeMargin
      ? toScaledCssLength(resolvedStyle.marginTop, scale)
      : undefined,
    marginRight: includeMargin
      ? toScaledCssLength(resolvedStyle.marginRight, scale)
      : undefined,
    marginBottom: includeMargin
      ? toScaledCssLength(resolvedStyle.marginBottom, scale)
      : undefined,
    marginLeft: includeMargin
      ? toScaledCssLength(resolvedStyle.marginLeft, scale)
      : undefined,
    padding: toScaledCssLength(resolvedStyle.padding, scale),
    paddingTop: toScaledCssLength(resolvedStyle.paddingTop, scale),
    paddingRight: toScaledCssLength(resolvedStyle.paddingRight, scale),
    paddingBottom: toScaledCssLength(resolvedStyle.paddingBottom, scale),
    paddingLeft: toScaledCssLength(resolvedStyle.paddingLeft, scale),
  };
}

function getResolvedEffectsSx(
  resolvedStyle: Record<string, unknown>,
  options?: { omitBackgroundImage?: boolean },
): Record<string, unknown> {
  // Text primitives paint backgroundImage on the glyph layer (clip), not the wrapper
  return {
    backgroundImage: options?.omitBackgroundImage
      ? undefined
      : (resolvedStyle.backgroundImage as string | undefined),
    filter: resolvedStyle.filter as string | undefined,
    boxShadow: resolvedStyle.boxShadow as string | undefined,
    textShadow: resolvedStyle.textShadow as string | undefined,
  };
}

function getRuntimeClipRectFromDom(
  nodeElement: HTMLElement,
  canvasElement: HTMLDivElement,
  scale: number,
): ClipRect {
  const nodeRect = nodeElement.getBoundingClientRect();
  const canvasRect = canvasElement.getBoundingClientRect();
  return {
    left: (nodeRect.left - canvasRect.left) / scale,
    top: (nodeRect.top - canvasRect.top) / scale,
    right: (nodeRect.right - canvasRect.left) / scale,
    bottom: (nodeRect.bottom - canvasRect.top) / scale,
  };
}

function getNodePositionMode(node: LayoutNode): "relative" | "absolute" {
  if (typeof node.style?.position === "string") {
    return node.style.position.toLowerCase() === "relative"
      ? "relative"
      : "absolute";
  }
  return "absolute";
}

function collectProtectedMediaSources(nodes: readonly LayoutNode[]): string[] {
  const sources = new Set<string>();

  const visit = (node: LayoutNode) => {
    if (
      node.kind === "primitive" &&
      (node.primitive === "image" || node.primitive === "video") &&
      typeof node.src === "string" &&
      isProtectedMediaSrc(node.src)
    ) {
      sources.add(node.src);
    }

    if (node.kind === "container") {
      node.children.forEach(visit);
      return;
    }

    if (node.kind === "component" && node.component === "layoutList") {
      const props = node.props as LayoutListComponentProps | undefined;
      props?.rowTemplate.children.forEach(visit);
      props?.rowVariants.forEach((variant) => {
        variant.template.children.forEach(visit);
      });
    }
  };

  nodes.forEach(visit);
  return [...sources];
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

interface LayoutRendererProps {
  document: LayoutDocument;
  data: InjectedData;
  /** Scale factor (defaults to 1). Useful when previewing a 1920px layout inside a 960px panel. */
  scale?: number;
  /** Visual scale applied outside the renderer when a parent wrapper handles zoom. */
  viewportScale?: number;
  /** Disable behavior graph execution while keeping the static layout render intact. */
  disableBehaviors?: boolean;
  /**
   * When true, scene catalog runtimes use a private drain coordinator so
   * multiple previews on one page do not overwrite each other.
   */
  isolateBehaviorCoordinator?: boolean;
  previewTickByNodeId?: Record<string, number>;
  isEditor?: boolean;
  onNodeFrameChange?: (
    nodeId: string,
    frame: { x: number; y: number; width: number; height: number } | null,
  ) => void;
}

/**
 * Renders a full LayoutDocument on a sized canvas.
 */
const LayoutRendererInner: React.FC<LayoutRendererProps> = ({
  document: doc,
  data,
  scale = 1,
  viewportScale,
  disableBehaviors = false,
  previewTickByNodeId,
  isEditor = false,
  onNodeFrameChange,
}) => {
  const behaviorDrainCoordinator = useBehaviorDrainCoordinator();
  const { canvas } = doc.meta;
  const canvasRef = useRef<HTMLDivElement>(null);
  const { handleOpenRuntimeContextMenu, contextMenu } =
    useRuntimeLayoutFullscreenMenu(canvasRef, isEditor);
  const getLayoutFontFaces = useLayoutFontFacesProvider();
  useLayoutFontFacesLoader(getLayoutFontFaces);
  const protectedMediaSources = useMemo(
    () => collectProtectedMediaSources(doc.scene.children),
    [doc.scene.children],
  );
  const protectedMediaCache = useProtectedMediaCache(protectedMediaSources);

  useSceneCustomEventGraphs(disableBehaviors ? undefined : doc.scene.catalog);
  useSceneFunctionGraphs(disableBehaviors ? undefined : doc.scene.catalog);
  useSceneVariableGraphs(disableBehaviors ? undefined : doc.scene.catalog);

  useEffect(() => {
    behaviorDrainCoordinator.clearVariableOverrides();
  }, [behaviorDrainCoordinator, doc.meta.id]);

  return (
    <ProtectedMediaCacheContext.Provider value={protectedMediaCache}>
      <LayoutRenderScaleContext.Provider value={scale}>
        <Box
          ref={canvasRef}
          onContextMenu={handleOpenRuntimeContextMenu}
          sx={{
            width: canvas.width * scale,
            height: canvas.height * scale,
            position: "relative",
            overflow: "hidden",
            backgroundColor: doc.scene.backgroundColor ?? "#ffffff",
            transformOrigin: "top left",
          }}
        >
          {doc.scene.children.map((node) => (
            <NodeRenderer
              key={`${node.id}:${previewTickByNodeId?.[node.id] ?? 0}`}
              node={node}
              data={data}
              scale={scale}
              viewportScale={viewportScale ?? scale}
              canvasRef={canvasRef}
              sceneCatalog={disableBehaviors ? undefined : doc.scene.catalog}
              disableBehaviors={disableBehaviors}
              previewTickByNodeId={previewTickByNodeId}
              isEditor={isEditor}
              document={doc}
              isFlex={false}
              onNodeFrameChange={onNodeFrameChange}
            />
          ))}
          {contextMenu}
        </Box>
      </LayoutRenderScaleContext.Provider>
    </ProtectedMediaCacheContext.Provider>
  );
};

const LayoutRenderer: React.FC<LayoutRendererProps> = ({
  isolateBehaviorCoordinator = false,
  ...props
}) => (
  <BehaviorDrainCoordinatorProvider
    isolate={isolateBehaviorCoordinator}
    layoutId={props.document.meta.id}
  >
    <LayoutRendererInner {...props} />
  </BehaviorDrainCoordinatorProvider>
);

export default LayoutRenderer;

// ---------------------------------------------------------------------------
// Internal recursive renderer
// ---------------------------------------------------------------------------

interface NodeRendererProps {
  document: LayoutDocument;
  node: LayoutNode;
  data: InjectedData;
  scale: number;
  viewportScale: number;
  canvasRef: React.RefObject<HTMLDivElement | null>;
  sceneCatalog?: SceneBehaviorCatalog;
  previewTickByNodeId?: Record<string, number>;
  inFlow?: boolean;
  isEditor?: boolean;
  disableBehaviors?: boolean;
  parentOffsetX?: number;
  parentOffsetY?: number;
  /** Canvas-absolute origin for clip-path (differs from parentOffset when nested in flex padding box). */
  canvasOffsetX?: number;
  canvasOffsetY?: number;
  clipRect?: ClipRect;
  isFlex: boolean;
  disableAnimatedClipTracking?: boolean;
  onNodeFrameChange?: (
    nodeId: string,
    frame: { x: number; y: number; width: number; height: number } | null,
  ) => void;
}

export const NodeRenderer: React.FC<NodeRendererProps> = ({
  document,
  node,
  data,
  scale,
  viewportScale,
  canvasRef,
  previewTickByNodeId,
  sceneCatalog,
  inFlow = false,
  isEditor = false,
  disableBehaviors = false,
  parentOffsetX = 0,
  parentOffsetY = 0,
  canvasOffsetX,
  canvasOffsetY,
  clipRect,
  isFlex,
  disableAnimatedClipTracking = false,
  onNodeFrameChange,
}) => {
  const nodeTransform =
    typeof node.style?.transform === "string"
      ? node.style.transform
      : undefined;
  const absoluteX = node.x + parentOffsetX;
  const absoluteY = node.y + parentOffsetY;
  const currentNodeContext = useMemo(
    () => ({
      x: Math.round(absoluteX),
      y: Math.round(absoluteY),
      width: Math.round(node.width),
      height: Math.round(node.height),
      rotation: Math.round(node.rotation ?? 0),
      skewX: parseSkewAxis(nodeTransform, "x"),
      skewY: parseSkewAxis(nodeTransform, "y"),
    }),
    [
      absoluteX,
      absoluteY,
      node.height,
      node.rotation,
      node.width,
      nodeTransform,
    ],
  );
  const sceneAnimationsByName = useMemo(
    () =>
      disableBehaviors
        ? {}
        : extractSceneAnimations(sceneCatalog, data, currentNodeContext).byName,
    [currentNodeContext, data, disableBehaviors, sceneCatalog],
  );

  const {
    stylePatch: behaviorOverlay,
    styleBindingPatch: behaviorStyleBindingPatch,
    locationBindingPatch: behaviorLocationBindingPatch,
    transformBindingPatch: behaviorTransformBindingPatch,
    componentBindingPatch: behaviorComponentBindingPatch,
    textPatch: behaviorTextPatch,
    locationPatch: behaviorLocationPatch,
    transformPatch: behaviorTransformPatch,
    componentPatch: behaviorComponentPatch,
    bindingPatch,
    animation: behaviorAnimationPayload,
    hasRender: hasBehaviorRender,
  } = useBehavior(
    disableBehaviors ? undefined : node.behavior,
    data,
    currentNodeContext,
    sceneAnimationsByName,
  );
  const layoutBaseRef = useRef({
    rotation: 0,
    width: 0,
    height: 0,
    skewX: 0,
    skewY: 0,
  });

  const componentNode = useMemo(() => node as ComponentNode, [node]);

  const resolvedComponentPatch = useMemo(
    () =>
      mergeBehaviorComponent(
        behaviorComponentPatch,
        behaviorComponentBindingPatch,
        data,
        node.kind === "component" && componentNode.component === "layoutList"
          ? node.dataBinding
          : undefined,
      ),
    [
      behaviorComponentPatch,
      behaviorComponentBindingPatch,
      data,
      componentNode.component,
      node.dataBinding,
      node.kind,
    ],
  );

  const barLoaderTargetPatches = useMemo(() => {
    const raw = (
      resolvedComponentPatch as { barLoaderTargetPatches?: unknown } | undefined
    )?.barLoaderTargetPatches;
    return Array.isArray(raw) ? (raw as BarLoaderTargetPatch[]) : undefined;
  }, [resolvedComponentPatch]);

  const componentPatchForProps = useMemo(() => {
    if (!resolvedComponentPatch) return undefined;
    const rest = { ...(resolvedComponentPatch as Record<string, unknown>) };
    delete rest.barLoaderTargetPatches;
    return Object.keys(rest).length > 0 ? rest : undefined;
  }, [resolvedComponentPatch]);

  const resolvedLocationPatch = useMemo(
    () =>
      isEditor
        ? undefined
        : mergeBehaviorLocation(
            behaviorLocationPatch,
            behaviorLocationBindingPatch,
            data,
          ),
    [behaviorLocationBindingPatch, behaviorLocationPatch, data, isEditor],
  );

  const resolvedTransformPatch = useMemo(
    () =>
      isEditor
        ? undefined
        : mergeBehaviorTransform(
            behaviorTransformPatch,
            behaviorTransformBindingPatch,
            data,
          ),
    [behaviorTransformBindingPatch, behaviorTransformPatch, data, isEditor],
  );

  const patchs = useMemo(() => {
    return {
      stylePatch: behaviorOverlay,
      styleBindingPatch: behaviorStyleBindingPatch,
      textPatch: behaviorTextPatch,
      locationPatch: resolvedLocationPatch,
      transformPatch: resolvedTransformPatch,
      componentPatch: componentPatchForProps,
      bindingPatch,
    };
  }, [
    behaviorOverlay,
    behaviorStyleBindingPatch,
    behaviorTextPatch,
    resolvedLocationPatch,
    resolvedTransformPatch,
    componentPatchForProps,
    bindingPatch,
  ]);

  const mergedStyle = React.useMemo(
    () =>
      mergeBehaviorStyles(
        node.style,
        patchs.stylePatch,
        patchs.styleBindingPatch,
        data,
      ),
    [node.style, patchs.stylePatch, patchs.styleBindingPatch, data],
  );
  const nodeWithBehaviorStyle = React.useMemo(() => {
    let n = { ...node, style: mergedStyle } as LayoutNode;
    if (!isEditor && hasSpacingPatch(mergedStyle)) {
      const sizeWithSpacing = applySpacingDeltaToSize({
        width: n.width,
        height: n.height,
        previousStyle: node.style,
        nextStyle: mergedStyle,
      });
      n = {
        ...n,
        width: sizeWithSpacing.width,
        height: sizeWithSpacing.height,
      } as LayoutNode;
    }
    if (patchs.locationPatch) {
      n = {
        ...n,
        x: patchs.locationPatch.x ?? n.x,
        y: patchs.locationPatch.y ?? n.y,
        zIndex:
          patchs.locationPatch.z != null
            ? Math.trunc(patchs.locationPatch.z)
            : n.zIndex,
      } as LayoutNode;
    }
    if (patchs.transformPatch) {
      n = {
        ...n,
        width: patchs.transformPatch.width ?? n.width,
        height: patchs.transformPatch.height ?? n.height,
        rotation: patchs.transformPatch.rotation ?? n.rotation,
      } as LayoutNode;
    }
    if (patchs.componentPatch && n.kind === "component") {
      n = {
        ...n,
        props: {
          ...(n.props ?? {}),
          ...patchs.componentPatch,
        },
      };
    }
    // If a setText action fired, override the node's parts with a literal text.
    if (
      patchs.textPatch !== undefined &&
      n.kind === "primitive" &&
      n.primitive === "text"
    ) {
      n = { ...n, text: patchs.textPatch };
    }
    return n;
  }, [isEditor, node, mergedStyle, patchs]);
  const renderedX =
    node.style?.position === "relative"
      ? 0
      : inFlow
        ? nodeWithBehaviorStyle.x
        : nodeWithBehaviorStyle.x + parentOffsetX;
  const renderedY =
    node.style?.position === "relative"
      ? 0
      : inFlow
        ? nodeWithBehaviorStyle.y
        : nodeWithBehaviorStyle.y + parentOffsetY;

  const canvasNodeLeft =
    canvasOffsetX != null ? canvasOffsetX + nodeWithBehaviorStyle.x : renderedX;
  const canvasNodeTop =
    canvasOffsetY != null ? canvasOffsetY + nodeWithBehaviorStyle.y : renderedY;
  const renderedNodeWidth = useMemo(() => {
    if (
      nodeWithBehaviorStyle.kind !== "component" ||
      nodeWithBehaviorStyle.component !== "barLoader"
    ) {
      return nodeWithBehaviorStyle.width;
    }
    const barLoaderProps = (nodeWithBehaviorStyle.props ??
      {}) as BarLoaderProps;
    const behaviorTotalWeight =
      typeof barLoaderProps.totalWeight === "number" &&
      Number.isFinite(barLoaderProps.totalWeight)
        ? barLoaderProps.totalWeight
        : undefined;
    return resolveBarLoaderVariableFootprintWidth({
      props: barLoaderProps,
      dataBinding: nodeWithBehaviorStyle.dataBinding,
      data,
      bindingPatch: patchs.bindingPatch,
      barLoaderTargetPatches,
      behaviorTotalWeight,
      fallbackWidth: nodeWithBehaviorStyle.width,
    });
  }, [
    barLoaderTargetPatches,
    data,
    nodeWithBehaviorStyle,
    patchs.bindingPatch,
  ]);
  const isPrimitiveTextFitWidth =
    nodeWithBehaviorStyle.kind === "primitive" &&
    nodeWithBehaviorStyle.primitive === "text" &&
    nodeWithBehaviorStyle.textWidthMode === "fitContent";
  const renderedZIndex = nodeWithBehaviorStyle.zIndex;
  layoutBaseRef.current = {
    rotation: nodeWithBehaviorStyle.rotation ?? 0,
    width: nodeWithBehaviorStyle.width,
    height: nodeWithBehaviorStyle.height,
    skewX: parseSkewAxis(nodeTransform, "x"),
    skewY: parseSkewAxis(nodeTransform, "y"),
  };

  const resolvedStyle = resolveStyles(nodeWithBehaviorStyle.style, data);
  const childPositionInFlex =
    isFlex &&
    typeof nodeWithBehaviorStyle.style?.position === "string" &&
    nodeWithBehaviorStyle.style.position.toLowerCase() === "relative"
      ? "relative"
      : "absolute";
  const shouldFlowInFlex = childPositionInFlex === "relative";

  const nodeRef = useRef<HTMLElement>(null);
  const frameTrackingSignature = useMemo(() => {
    if (
      nodeWithBehaviorStyle.kind === "component" &&
      nodeWithBehaviorStyle.component === "layoutList"
    ) {
      const listProps = (nodeWithBehaviorStyle.props ??
        {}) as Partial<LayoutListComponentProps>;
      const rowTemplate = listProps.rowTemplate;
      const variants = (listProps.rowVariants ?? [])
        .map(
          (variant) =>
            `${variant.name}:${variant.template.id}:${variant.template.width}x${variant.template.height}`,
        )
        .join("|");
      return [
        "layoutList",
        listProps.mode ?? "preview",
        listProps.editRowVariantName ?? "",
        rowTemplate
          ? `${rowTemplate.id}:${rowTemplate.width}x${rowTemplate.height}`
          : "no-row-template",
        variants,
      ].join("::");
    }
    return [
      nodeWithBehaviorStyle.kind,
      nodeWithBehaviorStyle.id,
      nodeWithBehaviorStyle.width,
      nodeWithBehaviorStyle.height,
    ].join("::");
  }, [nodeWithBehaviorStyle]);

  const [isAnimating, setIsAnimating] = useState(false);

  useLayoutEffect(() => {
    const element = nodeRef.current;
    if (!element || !behaviorAnimationPayload || !resolvedStyle.visibility) {
      setIsAnimating(false);
      return;
    }
    const animation = behaviorAnimationToWebAnimation(
      behaviorAnimationPayload,
      scale,
      layoutBaseRef.current,
    );
    const player = element.animate(animation.keyframes, animation.options);
    setIsAnimating(true);
    const stopAnimating = () => setIsAnimating(false);
    player.onfinish = stopAnimating;
    player.oncancel = stopAnimating;
    return () => {
      player.onfinish = null;
      player.oncancel = null;
      player.cancel();
      setIsAnimating(false);
    };
  }, [behaviorAnimationPayload, scale, resolvedStyle.visibility]);

  useLayoutEffect(() => {
    if (!onNodeFrameChange) return;
    let observer: ResizeObserver | null = null;
    let rafId: number | null = null;
    let disposed = false;

    const attach = () => {
      if (disposed) return;
      const element = nodeRef.current;
      const canvasElement = canvasRef.current;
      if (!element || !canvasElement) {
        rafId = requestAnimationFrame(attach);
        return;
      }

      const publishFrame = () => {
        const nodeRect = element.getBoundingClientRect();
        const canvasRect = canvasElement.getBoundingClientRect();
        onNodeFrameChange(node.id, {
          x: (nodeRect.left - canvasRect.left) / viewportScale,
          y: (nodeRect.top - canvasRect.top) / viewportScale,
          width: nodeRect.width / viewportScale,
          height: nodeRect.height / viewportScale,
        });
      };

      publishFrame();
      observer = new ResizeObserver(() => {
        publishFrame();
      });
      observer.observe(element);
    };

    attach();

    return () => {
      disposed = true;
      if (rafId != null) {
        cancelAnimationFrame(rafId);
      }
      observer?.disconnect();
      onNodeFrameChange(node.id, null);
    };
  }, [
    canvasRef,
    node.id,
    onNodeFrameChange,
    viewportScale,
    frameTrackingSignature,
  ]);

  const isLayoutListNode =
    nodeWithBehaviorStyle.kind === "component" &&
    nodeWithBehaviorStyle.component === "layoutList";

  const renderedClipPath =
    !inFlow && clipRect && !isLayoutListNode
      ? buildNodeClipPath(
          clipRect,
          canvasNodeLeft,
          canvasNodeTop,
          renderedNodeWidth,
          nodeWithBehaviorStyle.height,
          scale,
          parseRotationDegrees(
            composeNodeTransform(
              nodeWithBehaviorStyle.style?.transform,
              nodeWithBehaviorStyle.rotation,
            ),
          ),
        )
      : undefined;

  const renderData = data;

  if (!hasBehaviorRender) return null;

  if (resolvedStyle.visibility === false) return null;

  if (node.kind === "container") {
    return (
      <ContainerRenderer
        containerRef={nodeRef}
        document={document}
        node={nodeWithBehaviorStyle as ContainerNode}
        data={renderData}
        scale={scale}
        viewportScale={viewportScale}
        canvasRef={canvasRef}
        sceneCatalog={sceneCatalog}
        resolvedStyle={resolvedStyle}
        previewTickByNodeId={previewTickByNodeId}
        inFlow={inFlow}
        isEditor={isEditor}
        disableBehaviors={disableBehaviors}
        shouldTrackAnimatedClip={isAnimating && !disableAnimatedClipTracking}
        parentOffsetX={parentOffsetX}
        parentOffsetY={parentOffsetY}
        canvasOffsetX={canvasOffsetX}
        canvasOffsetY={canvasOffsetY}
        clipRect={clipRect}
        onNodeFrameChange={onNodeFrameChange}
      />
    );
  }

  if (node.kind === "component" && node.component === "layoutList") {
    return (
      <LayoutList
        nodeRef={nodeRef}
        node={nodeWithBehaviorStyle as ComponentNode}
        data={renderData}
        scale={scale}
        canvasRef={canvasRef}
        sceneCatalog={sceneCatalog}
        previewTickByNodeId={previewTickByNodeId}
        isEditor={isEditor}
        disableBehaviors={disableBehaviors}
        renderedX={renderedX}
        renderedY={renderedY}
        renderedZIndex={renderedZIndex}
        resolvedStyle={resolvedStyle}
        inFlow={inFlow}
        clipRect={clipRect}
        renderNode={({
          node: childNode,
          data: childData,
          parentOffsetX,
          parentOffsetY,
          clipRect: childClipRect,
          isFlex: childIsFlex,
          disableAnimatedClipTracking,
        }) => (
          <NodeRenderer
            isFlex={childIsFlex}
            document={document}
            node={childNode}
            data={childData}
            scale={scale}
            viewportScale={viewportScale}
            canvasRef={canvasRef}
            sceneCatalog={sceneCatalog}
            previewTickByNodeId={previewTickByNodeId}
            isEditor={isEditor}
            disableBehaviors={disableBehaviors}
            parentOffsetX={parentOffsetX}
            parentOffsetY={parentOffsetY}
            clipRect={childClipRect}
            disableAnimatedClipTracking={disableAnimatedClipTracking}
            onNodeFrameChange={isEditor ? undefined : onNodeFrameChange}
          />
        )}
      />
    );
  }

  const key = nodeKey(node);
  const entry = getRenderer(key);

  if (!entry) {
    return (
      <Box
        ref={nodeRef}
        sx={{
          ...(shouldFlowInFlex
            ? {
                position: "relative",
              }
            : {
                position: "absolute",
                left: renderedX * scale,
                top: renderedY * scale,
              }),
          width: isPrimitiveTextFitWidth
            ? "fit-content"
            : renderedNodeWidth * scale,
          height: nodeWithBehaviorStyle.height * scale,
          zIndex: renderedZIndex,
          border: "1px dashed #ccc",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#999",
          fontSize: 10,
          boxSizing: "border-box",
          clipPath: isFlex ? undefined : renderedClipPath,
          WebkitClipPath: isFlex ? undefined : renderedClipPath,
        }}
      >
        {key}
      </Box>
    );
  }

  const Component = entry.component;

  return (
    <Box
      key={`${node.id}`}
      ref={nodeRef}
      sx={{
        ...(inFlow || shouldFlowInFlex
          ? {
              position: "relative",
            }
          : {
              position: "absolute",
              left: renderedX * scale,
              top: renderedY * scale,
            }),
        width: isPrimitiveTextFitWidth
          ? "fit-content"
          : renderedNodeWidth * scale,
        height: nodeWithBehaviorStyle.height * scale,
        flexShrink: inFlow || shouldFlowInFlex ? 0 : undefined,
        zIndex: renderedZIndex,

        boxSizing: "border-box",
        clipPath: isFlex ? undefined : renderedClipPath,
        WebkitClipPath: isFlex ? undefined : renderedClipPath,
        ...getResolvedSpacingSx(resolvedStyle, scale, {
          includeMargin: inFlow,
        }),
        ...getResolvedEffectsSx(resolvedStyle, {
          omitBackgroundImage:
            nodeWithBehaviorStyle.kind === "primitive" &&
            nodeWithBehaviorStyle.primitive === "text" &&
            hasTextGradientFill(resolvedStyle.backgroundImage),
        }),
      }}
    >
      <Box
        sx={{
          width: "fit-content",
          height: nodeWithBehaviorStyle.height * scale,
          visibility: "hidden",
          pointerEvents: "none",
          position: "relative",
        }}
      >
        <Component
          document={document}
          node={nodeWithBehaviorStyle}
          data={renderData}
          bindingPatch={bindingPatch}
          barLoaderTargetPatches={barLoaderTargetPatches}
        />
      </Box>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          top: 0,
          left: 0,
          transform: composeNodeTransform(
            nodeWithBehaviorStyle.style?.transform,
            nodeWithBehaviorStyle.rotation,
          ),
          transformOrigin: "center center",
        }}
      >
        <Component
          document={document}
          node={nodeWithBehaviorStyle}
          data={renderData}
          bindingPatch={bindingPatch}
          barLoaderTargetPatches={barLoaderTargetPatches}
        />
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Container
// ---------------------------------------------------------------------------

interface ContainerRendererProps {
  document: LayoutDocument;
  containerRef: React.RefObject<HTMLElement>;
  node: ContainerNode;
  data: InjectedData;
  scale: number;
  viewportScale: number;
  canvasRef: React.RefObject<HTMLDivElement | null>;
  sceneCatalog?: SceneBehaviorCatalog;
  resolvedStyle: Record<string, unknown>;
  previewTickByNodeId?: Record<string, number>;
  animationStyle?: Record<string, string | number>;
  /** Combined declarative + behavior animation remount key. */
  animationInstanceKey?: string;
  inFlow?: boolean;
  isEditor?: boolean;
  disableBehaviors?: boolean;
  /** When true, rotation is driven by CSS keyframes (`transform`), not static `rotate`. */
  useKeyframeTransform?: boolean;
  parentOffsetX?: number;
  parentOffsetY?: number;
  canvasOffsetX?: number;
  canvasOffsetY?: number;
  clipRect?: ClipRect;
  shouldTrackAnimatedClip?: boolean;
  onNodeFrameChange?: (
    nodeId: string,
    frame: { x: number; y: number; width: number; height: number } | null,
  ) => void;
}

const ContainerRenderer: React.FC<ContainerRendererProps> = ({
  document,
  containerRef,
  node,
  data,
  scale,
  viewportScale,
  canvasRef,
  sceneCatalog,
  resolvedStyle,
  previewTickByNodeId,
  inFlow = false,
  isEditor = false,
  disableBehaviors = false,
  parentOffsetX = 0,
  parentOffsetY = 0,
  canvasOffsetX,
  canvasOffsetY,
  clipRect,
  shouldTrackAnimatedClip = false,
  onNodeFrameChange,
}) => {
  const transition = buildCssTransition(node.transitions);
  const [runtimeContainerClipRect, setRuntimeContainerClipRect] =
    useState<ClipRect | null>(null);
  const isFlex = node.layout === "flex";
  const hasRelativeChildren = useMemo(
    () =>
      node.children.some((child) => getNodePositionMode(child) === "relative"),
    [node.children],
  );
  const isWidthFitContent =
    isFlex && node.flexWidthMode === "fitContent" && hasRelativeChildren;
  const isHeightFitContent =
    isFlex && node.flexHeightMode === "fitContent" && hasRelativeChildren;
  const renderedContainerWidth = node.width;
  const renderedContainerHeight = node.height;
  const containerX = inFlow ? node.x : node.x + parentOffsetX;
  const containerY = inFlow ? node.y : node.y + parentOffsetY;
  const containerZIndex = node.zIndex;
  const childOffsetX = containerX;
  const childOffsetY = containerY;
  const borderInsets = getResolvedBorderSx(resolvedStyle);
  const paddingInsets = getResolvedSpacingSx(resolvedStyle, scale);
  const contentInsetX =
    Number(borderInsets.borderLeft ?? 0) +
    Number(paddingInsets.paddingLeft ?? 0);
  const contentInsetY =
    Number(borderInsets.borderTop ?? 0) + Number(paddingInsets.paddingTop ?? 0);
  const clipOriginX = canvasOffsetX ?? containerX;
  const clipOriginY = canvasOffsetY ?? containerY;
  const currentContainerClipRect: ClipRect = {
    left: clipOriginX + contentInsetX,
    top: clipOriginY + contentInsetY,
    right:
      clipOriginX +
      renderedContainerWidth -
      Number(borderInsets.borderRight ?? 0) -
      Number(paddingInsets.paddingRight ?? 0),
    bottom:
      clipOriginY +
      renderedContainerHeight -
      Number(borderInsets.borderBottom ?? 0) -
      Number(paddingInsets.paddingBottom ?? 0),
  };
  const canvasContentX = clipOriginX + contentInsetX;
  const canvasContentY = clipOriginY + contentInsetY;
  const childCanvasOffsetX = canvasContentX;
  const childCanvasOffsetY = canvasContentY;
  const effectiveContainerClipRect =
    runtimeContainerClipRect ?? currentContainerClipRect;
  const childClipRect = intersectClipRects(
    clipRect,
    effectiveContainerClipRect,
  );

  useLayoutEffect(() => {
    if (isFlex || !shouldTrackAnimatedClip) {
      setRuntimeContainerClipRect(null);
      return;
    }

    const element = containerRef.current;
    const canvasElement = canvasRef.current;
    if (!element || !canvasElement) return;

    let rafId: number | null = null;
    let cancelled = false;
    let idleFrames = 0;

    const frame = () => {
      if (cancelled) return;

      const runningAnimation = element
        .getAnimations()
        .some((animation) => animation.playState === "running");

      if (runningAnimation) {
        idleFrames = 0;
        const nextClipRect = getRuntimeClipRectFromDom(
          element,
          canvasElement,
          scale,
        );
        setRuntimeContainerClipRect(nextClipRect);
        rafId = requestAnimationFrame(frame);
      } else {
        idleFrames += 1;
        if (idleFrames < 8) {
          rafId = requestAnimationFrame(frame);
        } else {
          setRuntimeContainerClipRect(null);
        }
      }
    };

    frame();

    return () => {
      cancelled = true;
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [canvasRef, containerRef, isFlex, scale, shouldTrackAnimatedClip]);

  if (isFlex) {
    return (
      <Box
        key={`${node.id}`}
        ref={containerRef}
        sx={{
          ...(inFlow
            ? {
                position: "relative",
              }
            : {
                position: "absolute",
                left: containerX * scale,
                top: containerY * scale,
              }),
          width: isWidthFitContent
            ? "fit-content"
            : renderedContainerWidth * scale,
          height: isHeightFitContent
            ? "fit-content"
            : renderedContainerHeight * scale,
          flexShrink: inFlow ? 0 : undefined,
          zIndex: containerZIndex,
          display: "flex",
          flexDirection: node.flexDirection ?? "column",
          gap: node.gap != null ? `${node.gap * scale}px` : undefined,
          justifyContent: node.justifyContent ?? "flex-start",
          alignItems: node.alignItems ?? "center",
          backgroundColor: resolvedStyle.backgroundColor as string | undefined,
          ...getResolvedBorderSx(resolvedStyle),
          ...getResolvedSpacingSx(resolvedStyle, scale, {
            includeMargin: true,
          }),
          ...getResolvedEffectsSx(resolvedStyle),
          boxSizing: "border-box",
          transform: composeNodeTransform(node.style?.transform, node.rotation),
          transformOrigin: "center center",
          overflow: "hidden",
          transition: transition,
        }}
      >
        {[...node.children]
          .sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0))
          .map((child) => (
            <NodeRenderer
              document={document}
              key={`${child.id}:${previewTickByNodeId?.[child.id] ?? 0}`}
              node={child}
              data={data}
              scale={scale}
              viewportScale={viewportScale}
              canvasRef={canvasRef}
              sceneCatalog={sceneCatalog}
              previewTickByNodeId={previewTickByNodeId}
              isEditor={isEditor}
              disableBehaviors={disableBehaviors}
              clipRect={childClipRect}
              canvasOffsetX={canvasContentX}
              canvasOffsetY={canvasContentY}
              isFlex={true}
              inFlow={
                typeof child.style?.position === "string" &&
                child.style.position.toLowerCase() === "relative"
              }
              onNodeFrameChange={onNodeFrameChange}
            />
          ))}
      </Box>
    );
  }

  return (
    <>
      <Box
        key={`${node.id}::layer`}
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: containerZIndex,
          pointerEvents: "none",
        }}
      >
        <Box
          key={`${node.id}`}
          ref={containerRef}
          sx={{
            position: "absolute",
            left: containerX * scale,
            top: containerY * scale,
            width: renderedContainerWidth * scale,
            height: renderedContainerHeight * scale,
            flexShrink: inFlow ? 0 : undefined,
            zIndex: 0,
            backgroundColor: resolvedStyle.backgroundColor as
              | string
              | undefined,
            ...getResolvedBorderSx(resolvedStyle),
            ...getResolvedSpacingSx(resolvedStyle, scale, {
              includeMargin: true,
            }),
            ...getResolvedEffectsSx(resolvedStyle),
            boxSizing: "border-box",
            transform: composeNodeTransform(
              node.style?.transform,
              node.rotation,
            ),
            transformOrigin: "center center",
            overflow: "hidden",
            pointerEvents: "none",
            transition: transition,
          }}
        />
        {node.children.map((child) => (
          <NodeRenderer
            key={`${child.id}:${previewTickByNodeId?.[child.id] ?? 0}`}
            document={document}
            node={child}
            data={data}
            scale={scale}
            viewportScale={viewportScale}
            canvasRef={canvasRef}
            sceneCatalog={sceneCatalog}
            previewTickByNodeId={previewTickByNodeId}
            isEditor={isEditor}
            disableBehaviors={disableBehaviors}
            parentOffsetX={childOffsetX}
            parentOffsetY={childOffsetY}
            canvasOffsetX={childCanvasOffsetX}
            canvasOffsetY={childCanvasOffsetY}
            clipRect={childClipRect}
            isFlex={false}
            onNodeFrameChange={onNodeFrameChange}
          />
        ))}
      </Box>
    </>
  );
};
